<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref, watch } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { formatDate, mapOptionMultiselect } from "@/utils/helper";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { toast } from "vue-sonner";
import { useCategoryStore } from "@/stores/master/category-store";
import { useCategoryGroupStore } from "@/stores/master/category-group-store";
import { usePortStore } from "@/stores/master/port";
import { db } from "@/database/db";
import {
  RequisitionPayload,
  RequisitionResponse,
} from "@/types/interface/requisition";
import { useRequisitionStore } from "@/stores/management/requisition";
import Swal from "sweetalert2";

const idRequisition = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const requisitionStore = useRequisitionStore();
const categoryStore = useCategoryStore();
const groupCategoryStore = useCategoryGroupStore();
const portStore = usePortStore();
const listCategory = ref<any>([]);
const listGroupCategory = ref<any>([]);
const listPort = ref<any>([]);
const modalShow = ref<any>(false);
const queryTemp = ref<any>("");
const showProductForm = ref(false);
const requisitionDetail = ref<RequisitionResponse>();
const requisitionId = ref<number>();
let modalInstance: any;

const listUrgency = [
  { value: 1, label: "Regular" },
  { value: 2, label: "Urgent" },
];

const validationSchema = toTypedSchema(
  zod.object({
    vessel: zod.string().nonempty({ message: "Vessel is required" }),
    port: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    date: zod.string().nonempty({ message: "Date is required" }),
    category: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    group_category: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    urgency: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    remark: zod.string().nonempty({ message: "Remark is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: vessel } = useField<string>("vessel");
const { value: port } = useField<string>("port");
const { value: date } = useField<string>("date");
const { value: category } = useField<string>("category");
const { value: group_category } = useField<string>("group_category");
const { value: urgency } = useField<number>("urgency");
const { value: remark } = useField<string>("remark");

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const requisitionPayload: RequisitionPayload = {
    port_id: port.value?.value,
    category_id: category.value?.value,
    group_category_id: group_category.value?.value,
    created_at: date.value,
    remarks: remark.value,
    priority: urgency.value?.value,
  };

  const responseApi = ref<any>();

  if (isEdit.value == true) {
    responseApi.value = await requisitionStore.dispatchUpdate(
      idRequisition.value,
      requisitionPayload
    );
  } else {
    responseApi.value = await requisitionStore.dispatchCreate(
      requisitionPayload
    );
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    requisitionId.value = requisitionStore?.requisition?.data?.id;
    await db.requisition.put({
      requisition_id: requisitionId.value,
    });
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Requisition Successfully Updated"
          : "Requisition Successfully Added"
      );
      showProductForm.value = true;
      router.push("/management/requisition/add/" + requisitionId.value);
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});
const loadPort = async (query: string) => {
  let queryParam = "&keyword=" + query;
  const { success, message } = await portStore.dispatchGetAll(
    1,
    10,
    queryParam
  );
  if (success) {
    listPort.value = mapOptionMultiselect(
      await portStore.ports?.items,
      "id",
      "name",
      "country"
    );
  }
};

const loadCategory = async () => {
  const { success, message } = await categoryStore.dispatchGetCategories(1, 99);
  if (success) {
    listCategory.value = mapOptionMultiselect(
      await categoryStore.categories?.items,
      "id",
      "name"
    );
  }
};

const loadGroupCategory = async (idCategory = "") => {
  if (idCategory != "") {
    queryTemp.value = "&category_id=" + idCategory;
  }
  const { success, message } =
    await groupCategoryStore.dispatchGetCategoryGroups(1, 99, queryTemp.value);
  if (success) {
    listGroupCategory.value = mapOptionMultiselect(
      await groupCategoryStore.categoryGroups?.items,
      "id",
      "name"
    );
  }
};

const loadUserLogin = async () => {
  let getDataLogin = await db.storage.get("login-info");
  let userLogin = getDataLogin?.data;
  vessel.value = `${userLogin?.vessel?.name} (${userLogin?.vessel?.call_sign})`;
};

const handleSelectCategory = async () => {
  await loadGroupCategory(category.value?.value);
};

let debounceTimeoutPort: any;

const handleSearchPort = (query: string) => {
  clearTimeout(debounceTimeoutPort);
  debounceTimeoutPort = setTimeout(() => {
    loadPort(query);
  }, 500);
};

const loadRequisition = async () => {
  const { success, message } = await requisitionStore.dispatchDetail(
    idRequisition.value
  );
  if (success) {
    requisitionDetail.value = requisitionStore?.requisition?.data;
    category.value = mapOptionMultiselect(requisitionDetail.value.category);
    group_category.value = mapOptionMultiselect(
      requisitionDetail.value.groupCategory
    );
    port.value = mapOptionMultiselect(requisitionDetail.value.port);
    date.value = formatDate(requisitionDetail.value.created_at);
    requisitionId.value = requisitionDetail.value.id;
    remark.value = requisitionDetail.value.remarks;
    urgency.value = { value: requisitionDetail.value.priority_id, label: requisitionDetail.value.priority.toString() };
    showProductForm.value = true;
  }
};

onMounted(() => {
  loadCategory();
  loadGroupCategory();
  loadPort("");
  loadUserLogin();
  if (router.currentRoute.value.params.id != undefined) {
    isEdit.value = true;
    idRequisition.value = router.currentRoute.value.params.id.toString();
    loadRequisition();
  }
});

const backToListRequisition = () => {
  handleBeforeReloadModal("/management/requisition");
};

const handleBeforeReloadModal = (to: any) => {
  Swal.fire({
    title: "Are you sure want to leave requisition page ?",
    showDenyButton: true,
    showCancelButton: false,
    confirmButtonText: "Yes",
    denyButtonText: `No`,
    text: "All data will deleted, unless you already save before",
  }).then((result) => {
    if (result.isConfirmed) {
      router.push(to);
    }
  });
};

watch(modalShow, (newVal) => {
  if (modalInstance) {
    if (newVal) {
      modalInstance.show();
    } else {
      modalInstance.hide();
    }
  }
});
</script>

<template>
  <div class="row justify-content-center">
    <div class="col-lg-12">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0">Form Create Requisition</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              @click="backToListRequisition"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Requisition
            </a>
          </div>
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading"
          >
            <div class="row">
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="vessel" class="form-label label text-secondary"
                    >Vessel <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="vessel"
                      v-model="vessel"
                      placeholder="Vessel"
                      type="text"
                      class="form-control text-dark"
                      readonly
                    />
                  </div>
                  <span class="text-danger">{{ errors.vessel }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="port" class="form-label label text-secondary"
                    >Port <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="port"
                      :options="listPort"
                      placeholder="Choose Port"
                      @search="handleSearchPort"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.port }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="date" class="form-label label text-secondary"
                    >Date Receipt <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="date"
                      v-model="date"
                      placeholder="Date Receipt"
                      type="date"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.date }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="category" class="form-label label text-secondary"
                    >Category <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="category"
                      :options="listCategory"
                      placeholder="Choose Category"
                      @close="handleSelectCategory"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.category }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label
                    for="group_category"
                    class="form-label label text-secondary"
                    >Group Category <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="group_category"
                      :options="listGroupCategory"
                      placeholder="Choose Group Category"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.group_category }}</span>
                </div>
              </div>
              <div class="col-lg-4">
                <div class="form-group">
                  <label for="urgency" class="form-label label text-secondary"
                    >Urgency <span class="text-danger">*</span></label
                  >
                  <div class="form-group">
                    <vSelect
                      v-model="urgency"
                      :options="listUrgency"
                      placeholder="Choose Urgency"
                    ></vSelect>
                  </div>
                  <span class="text-danger">{{ errors.urgency }}</span>
                </div>
              </div>
              <div class="col-lg-12">
                <div class="form-group">
                  <label for="remark" class="form-label label text-secondary"
                    >Remark <span class="text-danger">*</span>
                  </label>
                  <div class="form-group">
                    <textarea
                      name="remark"
                      v-model="remark"
                      placeholder="Remark"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.date }}</span>
                </div>
              </div>
            </div>
            <button type="submit" class="btn btn-primary">
              {{
                isEdit ? "Update Form Requisition" : "Create Form Requisition"
              }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
